import { Capacitor, PluginListenerHandle, registerPlugin } from '@capacitor/core';

// ---- Events ----

export interface WebViewTerminatedEvent {
  terminatedAt: number;
  sessionDurationMs: number;
  reason: string;
  /** 'ios' | 'android' | 'browser' */
  platform: string;
  rendererPriorityAtExit?: 'waived' | 'bound' | 'important' | 'unknown';
  wasForeground?: boolean;
  msSinceForeground?: number;
  deviceModel?: string;
}

export interface ExternalLinkEvent {
  url: string;
  host: string;
  path: string;
  search: string;
}

// ---- Native Plugin Bridge ----

interface NativeCheckResult extends WebViewTerminatedEvent {
  wasTerminated: boolean;
}

interface NativeWebViewRecoveryPlugin {
  checkTermination(): Promise<NativeCheckResult>;
  addListener(
    eventName: 'externalLink',
    listenerFunc: (event: ExternalLinkEvent) => void,
  ): Promise<PluginListenerHandle>;
}

const NativePlugin = registerPlugin<NativeWebViewRecoveryPlugin>('WebViewRecovery');

export function onExternalLink(listener: (event: ExternalLinkEvent) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};
  const handlePromise = NativePlugin.addListener('externalLink', listener).catch((err) => {
    console.warn('[WebViewRecovery] externalLink listener unavailable:', err);
    return null;
  });
  return () => {
    handlePromise.then((handle) => handle?.remove()).catch(() => {});
  };
}

// ---- Browser Detection ----

const SESSION_START = 'btr_start';
const LAST_ACTIVITY = 'btr_activity';
const CLEAN_EXIT = 'btr_clean_exit';

function checkBrowserTermination(): WebViewTerminatedEvent | null {
  try {
    const hadSession = sessionStorage.getItem(SESSION_START) !== null;
    const cleanExit = sessionStorage.getItem(CLEAN_EXIT) !== null;

    if (hadSession && !cleanExit) {
      const now = Date.now();
      const startedAt = parseInt(sessionStorage.getItem(SESSION_START)!, 10);

      return {
        terminatedAt: now,
        sessionDurationMs: startedAt ? now - startedAt : -1,
        reason: 'renderer_crash',
        platform: 'browser',
      };
    }
  } catch {}

  return null;
}

function startBrowserTracking(): () => void {
  const now = Date.now();

  try {
    sessionStorage.setItem(SESSION_START, String(now));
    sessionStorage.removeItem(CLEAN_EXIT);
    sessionStorage.setItem(LAST_ACTIVITY, String(now));
  } catch {}

  const onActivity = (): void => {
    try {
      sessionStorage.setItem(LAST_ACTIVITY, String(Date.now()));
    } catch {}
  };
  const activityEvents = ['click', 'scroll', 'keydown', 'touchstart'];
  activityEvents.forEach((evt) => document.addEventListener(evt, onActivity, { passive: true }));

  const onCleanExit = (): void => {
    try {
      sessionStorage.setItem(CLEAN_EXIT, '1');
    } catch {}
  };
  window.addEventListener('pagehide', onCleanExit);
  window.addEventListener('beforeunload', onCleanExit);

  return () => {
    activityEvents.forEach((evt) => document.removeEventListener(evt, onActivity));
    window.removeEventListener('pagehide', onCleanExit);
    window.removeEventListener('beforeunload', onCleanExit);
  };
}

// ---- Unified API ----

type Listener = (event: WebViewTerminatedEvent) => void;

const listeners: Listener[] = [];
let initialized = false;
let cleanup: (() => void) | null = null;

export function onWebViewTerminated(listener: Listener): () => void {
  listeners.push(listener);

  if (!initialized) {
    initialized = true;

    if (Capacitor.isNativePlatform()) {
      NativePlugin.checkTermination()
        .then((result) => {
          if (result.wasTerminated) {
            const { wasTerminated, ...event } = result;
            listeners.forEach((fn) => fn(event));
          }
        })
        .catch((err) => {
          console.warn('[WebViewRecovery] checkTermination failed:', err);
        });
    } else {
      const event = checkBrowserTermination();
      if (event) {
        setTimeout(() => listeners.forEach((fn) => fn(event)), 0);
      }
      cleanup = startBrowserTracking();
    }
  }

  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

export function removeAllWebViewRecoveryListeners(): void {
  listeners.length = 0;
  if (cleanup) {
    cleanup();
    cleanup = null;
  }
  initialized = false;
}
